'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Select from '@pro/components/Widgets/Select.react';
import Alert from '@components/Widgets/Alert/Alert.react';
import Analytics from '@utils/Analytics';
import confirm from '@utils/Confirm';
import { getLoggedServingsOfRecipe, getMealAndLeftovers, getParticipantsForSpecificMeal, mealSortCompare } from '@utils/Meals';
import { getMealAndLeftovers as getMealAndLeftoversFromPlan } from '@utils/proMeals';
const getParticipantsForSpecificMealFromPlan = getParticipantsForSpecificMeal;
import WarningsModal from '@components/Planner/Modals/WarningsModal.react';
import { generateWarningsFromDishServings } from '@utils/YieldWarnings';
import {
    createScalingMap,
    calculateParticipantServings,
    calculateTotalServingsForParticipants,
    calculateRequiredScaling,
    calculateTotalYield,
    getBaseServings
} from '@utils/servingsCalculations';
import LeftoverDishesSection from './LeftoverDishesSection.react';
import ParticipantRow from './ParticipantRow.react';
import LeftoverAllocationsSection from './LeftoverAllocationsSection.react';
import DishImage from './DishImage.react';
import '@pro/components/Modals/Modals.scss';
import './EditMealServingsModalPrimary.scss';

export default class EditMealServingsModalPrimary extends Component {
    static propTypes = {
        meal: PropTypes.object,
        plan: PropTypes.object,
        contents: PropTypes.object,
        scaling: PropTypes.number,
        onChangeParticipants: PropTypes.func,
        closeModal: PropTypes.func,
        participants: PropTypes.array,
        editableParticipants: PropTypes.bool,
        profile: PropTypes.object,
        hideLeftoverOptions: PropTypes.bool,
    };

    static defaultProps = {
        editableParticipants: true,
        hideLeftoverOptions: false,
    };

    static contextTypes = {
        isPro: PropTypes.bool,
        user: PropTypes.object,
        meals: PropTypes.array,
        router: PropTypes.object,
        location: PropTypes.object,
        showMealDetails: PropTypes.func,
        addSwapContext: PropTypes.object,
    };

    constructor(props, context) {
        super(props, context);

        const { plan, mealDishes } = props;
        const { meals = [] } = context;

        const meal = mealDishes[0];
        const allMeals = plan ? getMealAndLeftoversFromPlan(meal, plan) : getMealAndLeftovers(meal, meals);

        let extraServings = 0;
        const dishes= [];
        if(mealDishes) {
            // Sort mealDishes to ensure consistent ordering with MealDetails display
            // This is defensive - ensures modal displays dishes in the same order regardless of data source
            const sortedMealDishes = [...mealDishes].sort((a, b) => mealSortCompare(a, b));

            sortedMealDishes.forEach(meal => {
                let dish = Object.assign({}, meal);
                if (dish.meal_type !== 'leftover') {
                    const leftovers = plan ? plan.items.filter(item => item.parent === dish.id && !item.deleted)
                        : meals.filter(item => item.parent_uuid === dish.uuid && !item.deleted);

                    dish = { ...dish, main: { ...meal }, leftovers: leftovers };
                    extraServings = dish.extra_servings || 0;
                    dishes.push(dish);
               } else {
                   // Include leftover dishes so they can be edited
                   dish = { ...dish, main: { ...meal }, leftovers: [], isLeftover: true };
                   dishes.push(dish);
               }
           });
        }

        const participants = props.participants.slice(); // Make a copy of the array

        this.state = {
            editLeftovers: false,
            allMeals,
            participants,
            dishes,
            extraServings,
            pendingLeftovers: null,
            leftoverDataUpdated: false,
            showWarningsModal: false,
            expandedParticipants: {}, // Track which participants have expanded collapse
            dirty: false,
        };
    }

    componentDidMount =  () => {
        const { meal } = this.props;
        Analytics.editMeal({"Meal UUID": meal ? (meal.uuid || meal.id) : null});
    }

    componentDidUpdate(prevProps) {
        const { participants, pendingLeftovers } = this.props;

        const oldUuids = prevProps.participants.map(item => item.uuid).sort();
        const newUuids = participants.map(item => item.uuid).sort();

        if (oldUuids.length !== newUuids.length || !oldUuids.every((uuid, index) => uuid === newUuids[index])) {
            this.setState({participants: participants.slice()});
        }

        // Handle pending leftovers data from return mode (hook-based system)
        if (pendingLeftovers && pendingLeftovers !== prevProps.pendingLeftovers) {
            // Check if leftovers actually changed by comparing with existing state
            const hasLeftoverChanges = this.hasLeftoverChanges(pendingLeftovers);

            this.setState({
                pendingLeftovers,
                extraServings: pendingLeftovers.extraServings || 0,
                leftoverDataUpdated: true,
                dirty: hasLeftoverChanges ? true : this.state.dirty
            }, this.recalculateScaling);
        }
    }

    /**
     * Check if pendingLeftovers represents actual changes from current state
     */
    hasLeftoverChanges = (pendingLeftovers) => {
        const { dishes, extraServings } = this.state;

        // Check if extra servings changed
        if (pendingLeftovers.extraServings !== extraServings) {
            return true;
        }

        // Get existing leftovers from dishes
        const existingLeftovers = dishes.flatMap(dish => dish.leftovers || []);

        // Check if number of leftovers changed
        const pendingLeftoverCount = pendingLeftovers.leftovers ? pendingLeftovers.leftovers.length : 0;
        if (pendingLeftoverCount !== existingLeftovers.length) {
            return true;
        }

        // If we have no leftovers in either, no changes
        if (pendingLeftoverCount === 0 && existingLeftovers.length === 0) {
            return false;
        }

        // If we got here and have pending leftovers, assume there are changes
        // (A deep comparison would be complex and might not be necessary)
        return pendingLeftoverCount > 0;
    }

    /**
     * Helper method to calculate leftover servings from either pendingLeftovers or existing leftovers
     * @param {Object} dish - The dish object (with main property in recalculateScaling, or direct dish in getTotalServings)
     * @param {Object} dishContent - The content object for the dish
     * @param {Array} existingLeftovers - Array of existing leftover meals (optional)
     * @returns {number} Total leftover servings
     */
    calculateLeftoverServings = (dish, dishContent, existingLeftovers = null) => {
        const { pendingLeftovers } = this.state;
        const { plan } = this.props;
        const { user } = this.context;

        let leftoverServings = 0;

        // Use pending leftovers as source of truth if they exist
        if (pendingLeftovers && pendingLeftovers.leftovers) {
            leftoverServings = pendingLeftovers.leftovers.reduce((sum, leftover) => {
                const { participants: leftoverParticipants } = leftover;
                const leftoverSlotServings = calculateTotalServingsForParticipants(leftoverParticipants, dish, dishContent);
                return sum + leftoverSlotServings;
            }, 0);
        } else if (existingLeftovers && existingLeftovers.length > 0) {
            // For existing leftovers: read logged_amount from EACH leftover meal
            existingLeftovers.forEach(leftover => {
                const leftoverParticipants = plan ? getParticipantsForSpecificMealFromPlan(leftover, user) : getParticipantsForSpecificMeal(leftover, user);
                leftoverServings += calculateTotalServingsForParticipants(leftoverParticipants, leftover, dishContent);
            });
        }

        return leftoverServings;
    }

    getTotalServings = () => {
        const { dishes, participants, pendingLeftovers, extraServings } = this.state;
        const { contents, plan } = this.props;
        const { user } = this.context;

        const dishServings = [];
        
        dishes.forEach(({main: dish, leftovers}) => {
            const dishContent = contents[dish.recipe_uuid || dish.food_uuid];

            // Calculate servings for current meal participants
            const currentMealServings = calculateTotalServingsForParticipants(participants, dish, dishContent);

            // Calculate leftover servings using helper method
            const leftoverServings = this.calculateLeftoverServings(dish, dishContent, leftovers);
            const totalServings = currentMealServings + leftoverServings;

            // totalLeftoverServings is the same as leftoverServings in this context
            const totalLeftoverServings = leftoverServings;
            const servings = getBaseServings(dishContent);
            // Always calculate scaling from current participants + leftovers + extraServings
            const scaling = calculateRequiredScaling(totalServings + extraServings, servings);
            const totalYield = calculateTotalYield(scaling, servings);
            const uuid = dish.uuid || dish.id;
            const title = contents[dish.recipe_uuid]?.title || contents[dish.food_uuid]?.name;

            dishServings.push({uuid, title, totalYield, totalServings, totalLeftoverServings, scaling});
        });

        return dishServings;
    }

    isParticipating = (participant) => {
        const { participants } = this.state;

        const result = participants.find(p => {
            if (participant.uuid && p.uuid === participant.uuid) {
                return true;
            }

            if (p === participant) {
                return true;
            }

            return false;
        })

        return result ? true : false;
    }

    recalculateScaling = () => {
        let { dishes, participants, extraServings } = this.state;
        const { contents } = this.props;

        // Recalculate scaling directly from participants without using existing dish.scaling
        const updatedDishes = dishes.map((dish) => {
            const dishContent = contents[dish.main.recipe_uuid || dish.main.food_uuid];
            const servings = getBaseServings(dishContent);

            // Calculate servings needed for current participants
            const currentMealServings = calculateTotalServingsForParticipants(participants, dish.main, dishContent);

            // Calculate leftover servings using helper method
            const leftoverServings = this.calculateLeftoverServings(dish.main, dishContent, dish.leftovers);
            const totalServings = currentMealServings + leftoverServings;

            // Calculate new scaling based on total servings needed (including extra servings)
            const newScaling = calculateRequiredScaling(totalServings + extraServings, servings); // Default to 1 batch minimum if no participants and no extra servings

            return {
                ...dish,
                main: { ...dish.main, scaling: newScaling }
            };
        });

        this.setState({ dishes: updatedDishes });
    }

    refineParticipantNames = (participants) => {
        let i = 1;

        participants.forEach(member => {
            if (member.uuid) {
                return;
            }

            member.name = 'Guest ' + i;
            i++;
        });

        return participants;
    }

    onToggleParticipant = (member) => {
        let { participants, dishes } = this.state;
        let matchIndex;

        // If this is a UUID'd member (non-guest), find by ID; otherwise find by reference
        if (member.uuid) {
            matchIndex = participants.findIndex(m => m.uuid === member.uuid);
        } else {
            matchIndex = participants.indexOf(member);
        }

        // Check if member exists: index >= 0 means found, -1 means not found
        if (matchIndex >= 0) {
            // Remove member
            participants = participants.slice();
            participants.splice(matchIndex, 1);
        } else {
            // Add member
            participants = [...participants, member];
        }

        participants = this.refineParticipantNames(participants);

        this.setState({participants, dirty: true}, this.recalculateScaling);
    }

    onChangeNumberOfGuests = (guestCount) => {
        let { participants } = this.state;

        const nonGuestParticipants = participants.filter(member => member.uuid);

        const guests = [...Array(guestCount).keys()].map(number => {
            return {uuid: "", name: 'Guest ' + (number + 1), portion: 1};
        });

        participants = nonGuestParticipants.concat(guests);

        this.setState({participants, dirty: true}, this.recalculateScaling);
    }

    onChangeExtraServings = (count) => {
        this.setState({extraServings: count, dirty: true}, this.recalculateScaling);
    }

    onChangeParticipantPortion = (member, newPortion) => {
        let { participants, dishes } = this.state;
        const { contents } = this.props;

        // Find the participant and update their portion
        const participantIndex = participants.findIndex(p => {
            if (member.uuid && p.uuid === member.uuid) {
                return true;
            }
            if (p === member) {
                return true;
            }
            return false;
        });

        if (participantIndex !== -1) {
            participants = participants.slice();
            const updatedPortion = Math.max(0.25, newPortion);
            participants[participantIndex] = {
                ...participants[participantIndex],
                portion: updatedPortion
            };

            // If this is the primary user, also update the meal's logged_amount
            if (member.uuid === 'USER' && dishes.length > 0) {
                dishes = [...dishes];
                dishes[0] = {
                    ...dishes[0],
                    main: { ...dishes[0].main, logged_amount: updatedPortion, logged_unit: 'serving' }
                };

                this.setState({participants, dishes, dirty: true}, this.recalculateScaling);
            } else {
                this.setState({participants, dirty: true}, this.recalculateScaling);
            }
        }
    }

    onChangeDishPortion = (member, dishId, newServings) => {
        let { participants, dishes } = this.state;
        const { contents } = this.props;

        if (member.uuid === 'USER') {
            dishes = [...dishes];
            // Find dish by ID instead of index for order-independent lookup
            const dishWrapper = dishes.find(d =>
                (d.main.id && d.main.id === dishId) ||
                (d.main.uuid && d.main.uuid === dishId)
            );

            if (dishWrapper) {
                // Immutable update - avoid mutating live plan data
                const dishIndex = dishes.indexOf(dishWrapper);
                dishes[dishIndex] = {
                    ...dishWrapper,
                    main: { ...dishWrapper.main, logged_amount: Math.max(0.25, newServings), logged_unit: 'serving' }
                };

                this.setState({dishes, dirty: true}, this.recalculateScaling);
            }
        }
    }

    onSelectNonParticipant = (nonParticipant) => {
        if (!nonParticipant) {
            return;
        }

        if (nonParticipant === 'guest') {
            return this.onAddGuest();
        }

        return this.onToggleParticipant(nonParticipant);
    }

    onChangeParticipants = (skipPendingLeftovers = false) => {
        const { allMeals, participants, dishes, editLeftovers, pendingLeftovers, extraServings } = this.state;
        const { onChangeParticipants, meal } = this.props;

        // Check if there's at least one real participant (not just guests)
        const nonGuestParticipants = participants.filter(p => p.uuid);
        if (!nonGuestParticipants.length) {
            this.setState({alert: 'Please choose at least one family member for this meal'});
            return;
        }

        Analytics.saveMeal({
            "Edited Participans": this.props.participants.length != participants.length,
            "Participants": participants.length,
            "Meal UUID": meal ? (meal.uuid || meal.id) : null
        });

        // If we have pending leftovers, include them in the save operation
        let leftovers = null;
        let scalingMap = dishes.length ? createScalingMap(dishes) : null;
        let replaceMeals = [];

        if (pendingLeftovers && !skipPendingLeftovers) {
            // Filter out leftovers with no participants - they should be deleted
            leftovers = pendingLeftovers.leftovers.filter(leftover =>
                leftover.participants && leftover.participants.length > 0
            );
            // Merge the pending leftover scaling with existing scaling
            if (pendingLeftovers.scalingMap) {
                scalingMap = { ...scalingMap, ...pendingLeftovers.scalingMap };
            }
            // Collect conflicting meals based on user's conflict resolution choices
            // Only include meals where user chose "replace" action
            if (pendingLeftovers.conflictResolutions) {
                const replaceResolutions = pendingLeftovers.conflictResolutions.filter(resolution => resolution.action === 'replace');
                replaceMeals = replaceResolutions
                    .map(resolution => resolution.conflictingMeals || [])
                    .flat();
            }
        }
        // Pass the complete scalingMap so each dish gets its correct scaling
        // The wrapper handles both numeric scaling (single dish) and object scaling (multi-dish)
        // Using scalingMap allows per-dish scaling when dishes have different yields
        onChangeParticipants(participants, scalingMap, leftovers, replaceMeals, editLeftovers, this.props.returnLeftoversOnly, extraServings, dishes);
    }


    handleConflictResolution = (conflictResolutions) => {
        // This method receives conflict resolution data from ConfirmOverwriteModal when in return mode
        // Instead of applying changes immediately, it stores them as pending for user review
        this.setState({
            conflictResolutions: conflictResolutions,
            pendingLeftovers: {
                ...this.state.pendingLeftovers,
                conflictResolutions: conflictResolutions
            }
        });
    }

    toggleParticipantExpand = (participantKey) => {
        this.setState(prevState => ({
            expandedParticipants: {
                ...prevState.expandedParticipants,
                [participantKey]: !prevState.expandedParticipants[participantKey]
            }
        }));
    }

    renderParticipantV2 = (member, i) => {
        const { editableParticipants, contents } = this.props;
        const { dishes, expandedParticipants } = this.state;
        const participantKey = member.uuid || `guest-${i}`;

        return (
            <ParticipantRow
                key={i}
                member={member}
                index={i}
                isParticipating={this.isParticipating(member)}
                editableParticipants={editableParticipants}
                dishes={dishes}
                contents={contents}
                isExpanded={expandedParticipants[participantKey]}
                onToggleExpand={() => this.toggleParticipantExpand(participantKey)}
                onToggleParticipant={this.onToggleParticipant}
                onChangeParticipantPortion={this.onChangeParticipantPortion}
                onChangeDishPortion={this.onChangeDishPortion}
            />
        );
    }

    handleAddEditLeftovers = () => {
        // Set editLeftovers to true and immediately proceed with save
        // This will trigger the leftover dates modal in the parent component
        this.setState({editLeftovers: true}, () => {
            // Call the component's onChangeParticipants method
            // which will extract state values (including editLeftovers=true) and call the prop
            this.onChangeParticipants(false);
        });
    }

    hasLeftoverDishes = () => {
        const { dishes } = this.state;
        return dishes.some(dish => dish.isLeftover);
    }

    navigateToOriginalMeal = (parentUuid, leftoverDish) => {
        const { allMeals } = this.state;
        const { router, location, showMealDetails, meals: contextMeals } = this.context;
        const { pathname, query, hash } = location;
        const { plan } = this.props;

        // For consumer: use contextMeals (all meals) to find the parent
        // For PRO: use plan.items
        const allAvailableMeals = plan ? plan.items : contextMeals;

        const parentMeal = allAvailableMeals.find(m =>
            (plan ? m.id === parentUuid : m.uuid === parentUuid)
        );

        if (!parentMeal) {
            showMealDetails(allMeals, 0);
            return;
        }

        // Filter meals for the parent meal's date and meal type
        const parentMealSlot = allAvailableMeals.filter(m => {
            if (plan) {
                // For PRO: match by offset and meal
                return m.offset === parentMeal.offset && m.meal === parentMeal.meal && !m.deleted;
            } else {
                // For consumer: match by date and meal
                return m.date === parentMeal.date && m.meal === parentMeal.meal && !m.deleted;
            }
        });

        // Find all dishes in the parent meal (non-leftovers only)
        const parentDishes = parentMealSlot.filter(m => m.meal_type !== 'leftover');

        // Find the index of the dish that matches the leftover's recipe/food
        const leftoverRecipeUuid = leftoverDish?.recipe_uuid || leftoverDish?.food_uuid;

        const dishIndex = parentDishes.findIndex(dish => {
            const dishRecipeUuid = dish.recipe_uuid || dish.food_uuid;
            return dishRecipeUuid === leftoverRecipeUuid;
        });

        // If we found a matching dish, use its index; otherwise default to 0
        const targetDishIndex = dishIndex >= 0 ? dishIndex : 0;

        showMealDetails(parentMealSlot, targetDishIndex);
    }

    renderLeftoverAllocations = (dishServings) => {
        const { dishes, pendingLeftovers, extraServings } = this.state;
        const { plan, mealDishes, contents } = this.props;
        const { isPro, user } = this.context;

        return (
            <LeftoverAllocationsSection
                dishes={dishes}
                contents={contents}
                plan={plan}
                mealDishes={mealDishes}
                pendingLeftovers={pendingLeftovers}
                extraServings={extraServings}
                dishServings={dishServings}
                isPro={isPro}
                user={user}
                onAddEditLeftovers={this.handleAddEditLeftovers}
                onChangeExtraServings={this.onChangeExtraServings}
            />
        );
    }

    renderLeftoverDishes = () => {
        const { dishes, allMeals } = this.state;
        const { contents, plan } = this.props;

        return (
            <LeftoverDishesSection
                dishes={dishes}
                contents={contents}
                plan={plan}
                allMeals={allMeals}
                onNavigateToOriginalMeal={!plan ? this.navigateToOriginalMeal : null}
            />
        );
    }

    onSave = () => {
        this.setState({editLeftovers: false}, this.onChangeParticipants);
    }

    onViewYieldNotes = () => {
        this.setState({showWarningsModal: true});
    }

    closeWarningsModal = () => {
        this.setState({showWarningsModal: false});
    }

    handleCloseModal = async () => {
        const { closeModal } = this.props;
        const { dirty } = this.state;

        if (dirty) {
            const confirmed = await confirm(
                <p>You have unsaved changes. Are you sure you want to close without saving?</p>,
                {
                    acceptText: 'Close Without Saving',
                    rejectText: 'Cancel',
                }
            );

            if (confirmed) {
                closeModal();
            }
        } else {
            closeModal();
        }
    }

    render() {
        const { contents, closeModal, profile, content, plan } = this.props;
        const { allMeals, alert, participants, dishes, editLeftovers, pendingLeftovers, extraServings } = this.state;
        const { user, isPro } = this.context;

        const dishServings = this.getTotalServings();
        const warnings = generateWarningsFromDishServings(dishServings, extraServings);
        const hasWarnings = warnings.length > 0;

        const nonGuestParticipants = participants.filter(member => member.uuid);

        let hasNoLeftoversTag = [];
        const notParticipating = [];

        if (contents) {
            hasNoLeftoversTag = dishes.filter(
                ({main}) => contents[main.recipe_uuid || main.food_uuid]?.tags.includes('No Leftovers')
            ).map(({main}) => contents[main.recipe_uuid]?.title || contents[main.food_uuid]?.name);
        }

        const primaryUserIncluded = this.isParticipating({uuid: 'USER'});

        if (!primaryUserIncluded) {
            notParticipating.push({
                uuid: 'USER',
                name: profile.name || profile.first_name || profile.email,
                portion: profile.portion,
            });
        }
        profile.family.forEach(member => {
            if (!this.isParticipating(member)) {
                notParticipating.push(member);
            }
        });

        // Do we have a guest already?
        const guestCount = participants.filter(p => !p.uuid).length;

        return (
            <Modal isOpen={true}
                onRequestClose={this.handleCloseModal}
                closeModal={this.handleCloseModal}
                className="el-modal el-modal2"
                overlayClassName="el-modal-overlay"
                contentLabel="Edit Meal Servings"
                closeTimeoutMS={250}>

                <div className="el-modal-container el-modal2-container edit-meal-servings-v2-container">
                    <header className="modal-header">
                        <h2>Edit Meal Servings</h2>
                        <button onClick={this.handleCloseModal} className="el-modal-close-x">
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container">
                        {/* In this Meal Section */}
                        <div className="in-this-meal-section">
                            <div className="section-header section-row">
                                <span className="section-label">In this Meal</span>
                                {hasWarnings && (
                                    <button className="section-link" onClick={this.onViewYieldNotes}>View yield notes</button>
                                )}
                            </div>
                            <div className={this.hasLeftoverDishes() ? "meal-items leftovers" : "meal-items"}>
                                {this.hasLeftoverDishes() && <div className="leftover-badge">This meal contains leftovers</div>}
                                {this.hasLeftoverDishes() && this.renderLeftoverDishes()}
                                {dishServings.filter(({uuid}) => {
                                    // Filter out leftover dishes since they're rendered separately
                                    const dish = dishes.find(d => (d.uuid || d.id) === uuid);
                                    return dish && !dish.isLeftover;
                                }).map(({uuid, title, totalYield}, index) => {
                                    // Find the corresponding dish and content to get image
                                    const dish = dishes.find(d => (d.uuid || d.id) === uuid);
                                    const content = dish ? contents[dish.main.recipe_uuid || dish.main.food_uuid] : null;
                                    const imageUrl = content?.image;

                                    // Use original dish index if available, otherwise fall back to sequential
                                    const dishNumber = dish?.main?.originalDishIndex || (index + 1);

                                    return (
                                        <div key={index} className="meal-item">
                                            <DishImage imageUrl={imageUrl} title={title} />
                                            <div className="meal-item-details">
                                                <div className="meal-item-name">{title}</div>
                                                <div className="meal-item-yield">Dish {dishNumber} · Yield: {totalYield} servings</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Who's Eating This Meal Section */}
                        <div className="whos-eating-section">
                            <div className="section-row">
                                <span className="section-label">Who's Eating This Meal?</span>
                            </div>
                            {alert ? (
                                <Alert type="error" description={alert} />
                            ) : null}
                            <div className="participants-box">
                                {nonGuestParticipants.map(this.renderParticipantV2)}
                                {notParticipating.map(this.renderParticipantV2)}
                                {/* Guests section */}
                                {!isPro && (
                                    <div className="guest-row-wrap">
                                        <div className="participant-row guest-controls">
                                            <div className="participant-checkbox-area">
                                                <button
                                                    className="el-checkbox"
                                                    onClick={() => this.onChangeNumberOfGuests(guestCount > 0 ? 0 : 1)}
                                                    data-active={guestCount > 0}
                                                    data-editable={true}
                                                >
                                                    <span className="participant-name">Guests</span>
                                                </button>
                                            </div>
                                            <div className="guest-count-controls">
                                                <button
                                                    className="guest-btn"
                                                    onClick={() => {
                                                        this.onChangeNumberOfGuests(Math.max(0, guestCount - 1));
                                                    }}
                                                    disabled={guestCount <= 0}
                                                >
                                                    -
                                                </button>
                                                <span className="guest-count">{guestCount}</span>
                                                <button
                                                    className="guest-btn"
                                                    onClick={() => {
                                                        this.onChangeNumberOfGuests(guestCount + 1);
                                                    }}
                                                >
                                                    +
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Leftover Allocations Section - Only show if not leftover dishes, hideLeftoverOptions is false, and no "No Leftovers" tag */}
                        {!this.hasLeftoverDishes() && !this.props.hideLeftoverOptions && !hasNoLeftoversTag.length && !content?.tags.includes('No Leftovers') && this.renderLeftoverAllocations(dishServings)}

                        {/* Alerts */}
                        {hasNoLeftoversTag.length ? (
                            <Alert type="info">
                                {`Typically, ${hasNoLeftoversTag.join(', ')} ${hasNoLeftoversTag.length > 1 ? "aren't" : "isn't"} suited for storing as leftovers due to ${hasNoLeftoversTag.length > 1 ? "their" : "its"} limited shelf life.`}
                            </Alert>
                        ) : null}

                        {content?.tags.includes('No Leftovers') ? (
                            <Alert type="info">
                                {`Typically, ${content.title} isn't suited for storing as leftovers due to its limited shelf life.`}
                            </Alert>
                        ) : null}
                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={this.handleCloseModal}>Cancel</button>
                        <button className="el-modal-ok-btn" onClick={this.onSave}>Save</button>
                    </footer>
                </div>

                {this.state.showWarningsModal && (
                    <WarningsModal
                        isOpen={this.state.showWarningsModal}
                        closeModal={this.closeWarningsModal}
                        warnings={warnings}
                    />
                )}
            </Modal>
        );
    }
}